import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Trash2, Plus, FileText, Image as ImageIcon, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { RichTextEditor } from "@/components/admin/RichTextEditor";

interface AboutContentItem {
  id: number;
  content_type: "paragraph" | "stat";
  heading: string | null;
  subtitle: string | null;
  badge_text: string | null;
  section_heading: string | null;
  content: string | null;
  stat_value: string | null;
  stat_label: string | null;
  sort_order: number;
  image_url: string | null;
}

interface AboutContent {
  heading: string;
  subtitle: string;
  badgeText: string;
  sectionHeading: string;
  content: string[];
  stats: Array<{
    value: string;
    label: string;
  }>;
  imageUrl: string;
}

export function AboutEditor() {
  const [aboutContent, setAboutContent] = useState<AboutContent>({
    heading: "",
    subtitle: "",
    badgeText: "",
    sectionHeading: "",
    content: [""],
    stats: [{ value: "", label: "" }],
    imageUrl: "",
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const response = await api.get<AboutContentItem[]>("/api/about");
      if (response.success && response.data) {
        const paragraphs = response.data
          .filter((item) => item.content_type === "paragraph" && item.content)
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((item) => item.content || "");

        // Get heading, subtitle, badge text, section heading and image URL from the first paragraph item
        const firstParagraph = response.data
          .filter((item) => item.content_type === "paragraph")
          .sort((a, b) => a.sort_order - b.sort_order)[0];
        const heading = firstParagraph?.heading || "";
        const subtitle = firstParagraph?.subtitle || "";
        const badgeText = firstParagraph?.badge_text || "";
        const sectionHeading = firstParagraph?.section_heading || "";
        const imageUrl = firstParagraph?.image_url || "";

        const stats = response.data
          .filter((item) => item.content_type === "stat" && item.stat_value && item.stat_label)
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((item) => ({
            value: item.stat_value || "",
            label: item.stat_label || "",
          }));

        setAboutContent({
          heading,
          subtitle,
          badgeText,
          sectionHeading,
          content: paragraphs.length > 0 ? paragraphs as string[] : [""],
          stats: stats.length > 0 ? stats : [{ value: "", label: "" }],
          imageUrl,
        });
      }
    } catch (error) {
      alert("Failed to load about content");
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      console.log("AboutEditor - Starting save process");

      // First, delete all existing about content
      console.log("AboutEditor - Fetching existing content");
      const existingResponse = await api.get<AboutContentItem[]>("/api/about");
      console.log("AboutEditor - Existing content:", existingResponse);

      if (existingResponse.success && existingResponse.data) {
        console.log("AboutEditor - Deleting", existingResponse.data.length, "existing items");
        for (const item of existingResponse.data) {
          console.log("AboutEditor - Deleting item:", item.id);
          await api.delete(`/api/about/${item.id}`);
        }
      }

      // Then create new items
      let sortOrder = 0;

      // Create paragraph items
      console.log("AboutEditor - Creating paragraph items:", aboutContent.content);
      for (const paragraph of aboutContent.content) {
        if (paragraph.replace(/<[^>]*>/g, '').trim()) {
          const data = {
            content_type: "paragraph",
            heading: aboutContent.heading || null,
            subtitle: aboutContent.subtitle || null,
            badge_text: aboutContent.badgeText || null,
            section_heading: aboutContent.sectionHeading || null,
            content: paragraph,
            image_url: aboutContent.imageUrl || null,
            sort_order: sortOrder++,
          };
          console.log("AboutEditor - Posting paragraph:", data);
          await api.post("/api/about", data);
        }
      }

      // Create stat items
      console.log("AboutEditor - Creating stat items:", aboutContent.stats);
      for (const stat of aboutContent.stats) {
        if (stat.value && stat.label) {
          const data = {
            content_type: "stat",
            stat_value: stat.value,
            stat_label: stat.label,
            sort_order: sortOrder++,
          };
          console.log("AboutEditor - Posting stat:", data);
          await api.post("/api/about", data);
        }
      }

      alert("About content updated successfully");
    } catch (error) {
      console.error("AboutEditor - Save error:", error);
      alert("Failed to update about content");
    } finally {
      setLoading(false);
    }
  };

  const addContentParagraph = () => {
    setAboutContent((prev) => ({
      ...prev,
      content: [...prev.content, ""],
    }));
  };

  const removeContentParagraph = (index: number) => {
    setAboutContent((prev) => ({
      ...prev,
      content: prev.content.filter((_, i) => i !== index),
    }));
  };

  const updateContentParagraph = (index: number, value: string) => {
    setAboutContent((prev) => ({
      ...prev,
      content: prev.content.map((item, i) => (i === index ? value : item)),
    }));
  };

  const addStat = () => {
    setAboutContent((prev) => ({
      ...prev,
      stats: [...prev.stats, { value: "", label: "" }],
    }));
  };

  const removeStat = (index: number) => {
    setAboutContent((prev) => ({
      ...prev,
      stats: prev.stats.filter((_, i) => i !== index),
    }));
  };

  const updateStat = (index: number, field: "value" | "label", value: string) => {
    setAboutContent((prev) => ({
      ...prev,
      stats: prev.stats.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  };

  const updateImageUrl = (value: string) => {
    setAboutContent((prev) => ({
      ...prev,
      imageUrl: value,
    }));
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:3001"}/api/upload`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (result.success && result.data?.imageUrl) {
        updateImageUrl(result.data.imageUrl);
      } else {
        alert('Failed to upload image');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-serif text-3xl text-primary mb-2">About Page</h1>
          <p className="text-muted-foreground">Manage about page content and statistics</p>
        </div>

        <Button onClick={handleSave} disabled={loading}>
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      {/* Heading Section */}
      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="font-serif text-xl text-primary">Page Content</h2>
            <FileText className="w-5 h-5 text-muted-foreground" />
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Page Heading</Label>
              <Input
                value={aboutContent.heading}
                onChange={(e) => setAboutContent((prev) => ({ ...prev, heading: e.target.value }))}
                placeholder="Enter page heading"
              />
            </div>

            <div className="space-y-2">
              <Label>Subtitle</Label>
              <Input
                value={aboutContent.subtitle}
                onChange={(e) => setAboutContent((prev) => ({ ...prev, subtitle: e.target.value }))}
                placeholder="Enter subtitle"
              />
            </div>

            <div className="space-y-2">
              <Label>Badge Text</Label>
              <Input
                value={aboutContent.badgeText}
                onChange={(e) => setAboutContent((prev) => ({ ...prev, badgeText: e.target.value }))}
                placeholder="Enter badge text (e.g., About the Conference)"
              />
            </div>

            <div className="space-y-2">
              <Label>Section Heading</Label>
              <Input
                value={aboutContent.sectionHeading}
                onChange={(e) => setAboutContent((prev) => ({ ...prev, sectionHeading: e.target.value }))}
                placeholder="Enter section heading"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Image Section */}
      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="font-serif text-xl text-primary">About Image</h2>
            <ImageIcon className="w-5 h-5 text-muted-foreground" />
          </div>

          <div className="space-y-2">
            <Label>Upload Image</Label>
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleBrowseClick}
                disabled={uploading}
                className="flex-1"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? "Uploading..." : "Browse Files"}
              </Button>
              {aboutContent.imageUrl && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => updateImageUrl("")}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {aboutContent.imageUrl && (
            <div className="mt-4">
              <Label>Preview</Label>
              <div className="mt-2 rounded-lg overflow-hidden border">
                <img
                  src={aboutContent.imageUrl}
                  alt="About section preview"
                  className="w-full h-64 object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
              <div className="mt-2 text-xs text-muted-foreground break-all">
                {aboutContent.imageUrl}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Content Sections */}
      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="font-serif text-xl text-primary">Content Sections</h2>
            <Button variant="outline" size="sm" onClick={addContentParagraph}>
              <Plus className="w-4 h-4 mr-2" />
              Add Paragraph
            </Button>
          </div>

          {aboutContent.content.map((paragraph, index) => (
            <div key={index} className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Paragraph {index + 1}</Label>
                {aboutContent.content.length > 1 && (
                  <Button variant="outline" size="sm" onClick={() => removeContentParagraph(index)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <RichTextEditor
                content={paragraph}
                onChange={(val) => updateContentParagraph(index, val)}
                placeholder="Enter paragraph content"
              />
            </div>
          ))}
        </div>
      </Card>

      {/* Statistics */}
      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="font-serif text-xl text-primary">Statistics</h2>
            <Button variant="outline" size="sm" onClick={addStat}>
              <Plus className="w-4 h-4 mr-2" />
              Add Stat
            </Button>
          </div>

          {aboutContent.stats.map((stat, index) => (
            <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <Label>Value</Label>
                <Input
                  value={stat.value}
                  onChange={(e) => updateStat(index, "value", e.target.value)}
                  placeholder="e.g., 500+"
                />
              </div>
              <div>
                <Label>Label</Label>
                <Input
                  value={stat.label}
                  onChange={(e) => updateStat(index, "label", e.target.value)}
                  placeholder="e.g., Delegates"
                />
              </div>
              <div className="flex justify-end">
                {aboutContent.stats.length > 1 && (
                  <Button variant="outline" size="sm" onClick={() => removeStat(index)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Preview */}
      <Card className="p-6 bg-accent/10">
        <h2 className="font-serif text-xl text-primary mb-4">Preview</h2>
        <div className="space-y-4">
          {aboutContent.content
            .filter((p) => p.trim())
            .map((paragraph, index) => (
              <div
                key={index}
                className="text-muted-foreground leading-relaxed prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: paragraph }}
              />
            ))}

          {aboutContent.stats.filter((s) => s.value && s.label).length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
              {aboutContent.stats
                .filter((s) => s.value && s.label)
                .map((stat, index) => (
                  <div key={index} className="text-center p-4 bg-card rounded-lg">
                    <div className="font-serif text-3xl text-primary mb-2">{stat.value}</div>
                    <div className="text-sm uppercase tracking-widest text-muted-foreground">
                      {stat.label}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
